// File: frontend/src/components/Users/UserDetail.jsx
// User detail view component

import React from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  UserIcon, 
  EnvelopeIcon, 
  ShieldCheckIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const UserDetail = ({ user, roles, onEdit, onBack, onRefresh }) => {
  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Usuário não encontrado</p>
      </div>
    );
  }

  const role = roles.find(r => r.id === user.role_id || r.name === user.role_name);
  const permissions = role?.permissions || {};

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR });
    } catch (error) {
      return 'Data inválida';
    }
  };

  const getPermissionText = (permission) => {
    if (permission === true || permission === 'all') return 'Completo';
    if (permission === 'team') return 'Equipe';
    if (permission === 'own') return 'Próprios';
    if (permission === false) return 'Negado';
    return 'Personalizado';
  };

  const getPermissionColor = (permission) => {
    if (permission === true || permission === 'all') return 'text-green-600 bg-green-100';
    if (permission === 'team') return 'text-blue-600 bg-blue-100';
    if (permission === 'own') return 'text-yellow-600 bg-yellow-100';
    if (permission === false) return 'text-red-600 bg-red-100';
    return 'text-gray-600 bg-gray-100';
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Voltar à Lista
          </button>
        </div>
        <button
          onClick={onEdit}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
        >
          <PencilIcon className="h-4 w-4 mr-2" />
          Editar Usuário
        </button>
      </div>

      {/* User Profile Card */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-6">
          <div className="flex items-center space-x-6">
            {/* Avatar */}
            <div className="flex-shrink-0">
              <div className="h-20 w-20 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center">
                <span className="text-2xl font-bold text-white">
                  {user.first_name?.[0]}{user.last_name?.[0]}
                </span>
              </div>
            </div>
            
            {/* Basic Info */}
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold text-gray-900 truncate">
                {user.name || `${user.first_name} ${user.last_name}`}
              </h2>
              <p className="text-lg text-gray-600 truncate">
                {user.email}
              </p>
              <div className="mt-2 flex items-center space-x-4">
                <span className={`inline-flex items-center px-2.5 py-1.5 rounded-full text-sm font-medium ${
                  user.is_active 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {user.is_active ? (
                    <>
                      <CheckCircleIcon className="h-4 w-4 mr-1" />
                      Ativo
                    </>
                  ) : (
                    <>
                      <XCircleIcon className="h-4 w-4 mr-1" />
                      Inativo
                    </>
                  )}
                </span>
                {role && (
                  <span className="inline-flex items-center px-2.5 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    <ShieldCheckIcon className="h-4 w-4 mr-1" />
                    {role.name}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Account Information */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <UserIcon className="h-5 w-5 mr-2 text-gray-400" />
            Informações da Conta
          </h3>
          
          <div className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Nome Completo</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {user.name || `${user.first_name} ${user.last_name}`}
              </dd>
            </div>
            
            <div>
              <dt className="text-sm font-medium text-gray-500">Email</dt>
              <dd className="mt-1 text-sm text-gray-900 flex items-center">
                <EnvelopeIcon className="h-4 w-4 mr-2 text-gray-400" />
                {user.email}
              </dd>
            </div>
            
            <div>
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  user.is_active 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {user.is_active ? 'Ativo' : 'Inativo'}
                </span>
              </dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-gray-500">Perfil de Acesso</dt>
              <dd className="mt-1">
                {role ? (
                  <div>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {role.name}
                    </span>
                    <p className="text-xs text-gray-600 mt-1">{role.description}</p>
                  </div>
                ) : (
                  <span className="text-sm text-red-600">Perfil não encontrado</span>
                )}
              </dd>
            </div>
          </div>
        </div>

        {/* Account History */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <ClockIcon className="h-5 w-5 mr-2 text-gray-400" />
            Histórico da Conta
          </h3>
          
          <div className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <CalendarIcon className="h-4 w-4 mr-2" />
                Criado em
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {formatDate(user.created_at)}
              </dd>
            </div>
            
            {user.updated_at && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <ClockIcon className="h-4 w-4 mr-2" />
                  Última atualização
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(user.updated_at)}
                </dd>
              </div>
            )}

            {user.last_login_at && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Último acesso</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(user.last_login_at)}
                </dd>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Permissions Details */}
      {role && permissions && Object.keys(permissions).length > 0 && (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <ShieldCheckIcon className="h-5 w-5 mr-2 text-gray-400" />
            Detalhes das Permissões
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(permissions)
              .filter(([key]) => key !== 'scope')
              .map(([module, modulePermissions]) => (
                <div key={module} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 capitalize mb-3">
                    {module === 'leads' ? 'Leads' :
                     module === 'contacts' ? 'Contatos' :
                     module === 'companies' ? 'Empresas' :
                     module === 'opportunities' ? 'Oportunidades' :
                     module === 'activities' ? 'Atividades' :
                     module === 'reports' ? 'Relatórios' :
                     module === 'users' ? 'Usuários' :
                     module === 'settings' ? 'Configurações' :
                     module}
                  </h4>
                  
                  <div className="space-y-2">
                    {typeof modulePermissions === 'object' && modulePermissions !== null ? 
                      Object.entries(modulePermissions).map(([action, allowed]) => (
                        <div key={action} className="flex items-center justify-between text-sm">
                          <span className="text-gray-600 capitalize">
                            {action === 'read' ? 'Visualizar' :
                             action === 'create' ? 'Criar' :
                             action === 'update' ? 'Editar' :
                             action === 'delete' ? 'Excluir' :
                             action === 'assign' ? 'Atribuir' :
                             action}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPermissionColor(allowed)}`}>
                            {getPermissionText(allowed)}
                          </span>
                        </div>
                      )) :
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Acesso Geral</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPermissionColor(modulePermissions)}`}>
                          {getPermissionText(modulePermissions)}
                        </span>
                      </div>
                    }
                  </div>
                </div>
              ))}
          </div>

          {permissions.scope && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Escopo de Acesso</h4>
              <p className="text-sm text-gray-600">
                {permissions.scope === 'global' 
                  ? 'Este usuário tem acesso a todos os dados do sistema, independente do proprietário.'
                  : permissions.scope === 'team'
                  ? 'Este usuário tem acesso aos dados da sua equipe e subordinados.'
                  : permissions.scope === 'own'
                  ? 'Este usuário tem acesso apenas aos seus próprios dados.'
                  : `Escopo personalizado: ${permissions.scope}`
                }
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserDetail;